import React, { forwardRef } from 'react';
import cx from 'classnames';
import PositionIcon, { SLOT_TO_POSITION, POSITION_CN } from '../europe/PositionIcon';
import css from './ExportCard.module.scss';

// ExportCard：专门用于「一键导出战术板」的离屏卡片。
// - 不出现在用户视野（CoachApp 用 position: fixed; left: -99999px 把它移出屏幕）
// - html2canvas 截图时直接拍这张卡 → 得到一张干净、清晰、能微信群直发的复盘图
// - 设计尺寸 1600×900（16:9），无论用户屏多大，导出图都是固定 HD 比例
//
// 数据来源：直接吃 CoachApp 已有的 bo / draft 状态，零额外抓取
const ExportCard = forwardRef(({ bo, draft, exportedAtFormatted }, ref) => {
  return (
    <div ref={ref} className={css.Card}>
      {/* ===== 顶部 Header ===== */}
      <div className={css.Header}>
        <div className={css.HeaderLeft}>
          <div className={css.Brand}>LoL Coach</div>
          <div className={css.Title}>战术板复盘</div>
        </div>
        <div className={css.HeaderRight}>
          <div className={css.HeaderMain}>
            {bo.mode}
            <span className={css.HeaderDim}>·</span>
            Game <strong>{bo.currentGameNumber}</strong>
            <span className={css.HeaderDim}>/{bo.totalGames}</span>
          </div>
          <div className={css.HeaderSub}>{exportedAtFormatted}</div>
        </div>
      </div>

      {/* ===== 状态条 ===== */}
      <div className={css.MetaBar}>
        <Chip variant={bo.fearlessEnabled ? 'success' : 'muted'}>
          Fearless · {bo.fearlessEnabled ? '已开启' : '关闭'}
        </Chip>
        <Chip variant={bo.currentFirstSide === 'blue' ? 'blue' : 'red'}>
          本局先手 · {bo.currentFirstSide === 'blue' ? '蓝方' : '红方'}
        </Chip>
        <Chip variant="gold">
          系列进度 · {bo.currentGameNumber}/{bo.totalGames}
        </Chip>
        {draft.isFinished && (
          <Chip variant="success">本局 BP 完成 ✓</Chip>
        )}
        {bo.globalLockedMap.size > 0 && (
          <Chip variant="muted">
            全局锁池 · {bo.globalLockedMap.size} 名
          </Chip>
        )}
      </div>

      {/* ===== 双方主体 ===== */}
      <div className={css.Body}>
        <TeamPanel side="blue" team={draft.blueTeam} />
        <div className={css.VsDivider}>
          <span className={css.VsLine} />
          <span className={css.VsText}>VS</span>
          <span className={css.VsLine} />
        </div>
        <TeamPanel side="red" team={draft.redTeam} />
      </div>

      {/* ===== Fearless 全局锁池（只在 fearless 且有锁时显示） ===== */}
      {bo.globalLockedMap.size > 0 && (
        <FearlessPool map={bo.globalLockedMap} />
      )}

      {/* ===== 已完成局摘要 ===== */}
      {bo.completedGames.length > 0 && (
        <CompletedGamesRow games={bo.completedGames} />
      )}

      {/* ===== Footer ===== */}
      <div className={css.Footer}>
        Generated by lol-pick-ban-ui · Coach Mode
      </div>
    </div>
  );
});

// ---- 小型展示组件 ----

const Chip = ({ children, variant = 'default' }) => (
  <span className={cx(css.Chip, css[`Chip_${variant}`])}>{children}</span>
);

const TeamPanel = ({ side, team }) => {
  const sideLabel = side === 'blue' ? '蓝方 BLUE' : '红方 RED';
  return (
    <div className={cx(css.Team, css[`Team_${side}`])}>
      <div className={css.TeamHeader}>
        <span className={css.TeamName}>{sideLabel}</span>
      </div>

      {/* BAN 排 */}
      <div className={css.BansRow}>
        <span className={css.RowLabel}>BAN</span>
        <div className={css.BansList}>
          {team.bans.map((b, i) => (
            <BanSlot key={i} index={i + 1} champion={b.champion} side={side} />
          ))}
        </div>
      </div>

      {/* PICK 排 */}
      <div className={css.PicksRow}>
        {team.picks.map((p, i) => (
          <PickCard
            key={i}
            index={i + 1}
            champion={p.champion}
            position={SLOT_TO_POSITION[i]}
            side={side}
          />
        ))}
      </div>
    </div>
  );
};

const BanSlot = ({ index, champion, side }) => (
  <div className={cx(css.BanSlot, css[`BanSlot_${side}`], champion && css.BanSlotFilled)}>
    {champion ? (
      <>
        <img
          src={champion.squareImg}
          alt={champion.name}
          crossOrigin="anonymous"
          className={css.BanImg}
        />
        <span className={css.BanCross} aria-hidden="true">×</span>
      </>
    ) : (
      <span className={css.BanIndex}>B{index}</span>
    )}
  </div>
);

const PickCard = ({ index, champion, position, side }) => (
  <div className={cx(css.PickCard, champion && css.PickCardFilled, css[`PickCard_${side}`])}>
    <div className={css.PickImgWrap}>
      {champion ? (
        <img
          src={champion.loadingImg}
          alt={champion.name}
          crossOrigin="anonymous"
          // HTML width/height 属性兜底：即使 CSS 被绕过也按 160×290 绘制
          // 160×290 比例 0.552 ≈ 立绘原版 308×560 比例 0.550，立绘完整不裁切
          width="160"
          height="290"
          className={css.PickImg}
        />
      ) : (
        <div className={css.PickEmpty}>—</div>
      )}
      <div className={css.PickSlotNum}>P{index}</div>
    </div>
    <div className={css.PickFooter}>
      <div className={css.PickName}>{champion ? champion.name : '空缺'}</div>
      <div className={cx(css.PickPos, css[`PickPos_${side}`])}>
        <PositionIcon position={position} size={16} />
        <span className={css.PickPosLabel}>{POSITION_CN[position]}</span>
      </div>
    </div>
  </div>
);

const FearlessPool = ({ map }) => {
  const entries = Array.from(map.entries()); // [championId, { gameNumber, side, championName }]
  return (
    <div className={css.PoolBlock}>
      <div className={css.PoolTitle}>
        Fearless 全局锁池 · {entries.length} 名
      </div>
      <div className={css.PoolList}>
        {entries.map(([id, info]) => (
          <span
            key={id}
            className={cx(css.PoolItem, css[`PoolItem_${info.side}`])}
          >
            <span className={css.PoolItemGame}>G{info.gameNumber}</span>
            <span className={css.PoolItemSide}>
              {info.side === 'blue' ? '蓝' : '红'}
            </span>
            <span className={css.PoolItemName}>{info.championName}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const CompletedGamesRow = ({ games }) => (
  <div className={css.CompletedBlock}>
    <div className={css.CompletedTitle}>
      已完成 · {games.length} 局
    </div>
    <div className={css.CompletedList}>
      {games.map((g) => (
        <div key={g.gameIndex} className={css.CompletedItem}>
          <span className={css.CompletedNum}>G{g.gameIndex + 1}</span>
          <span className={cx(css.CompletedSide, css[`CompletedSide_${g.firstSide}`])}>
            {g.firstSide === 'blue' ? '蓝先手' : '红先手'}
          </span>
          <span className={css.CompletedCount}>
            B{(g.bluePicks || []).length}|R{(g.redPicks || []).length}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default ExportCard;
